import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { setToken } from '../utils/auth';
import axios from 'axios';
import Footer from '../components/Footer';

// const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:6001/api'; // Local development
const API_URL = process.env.REACT_APP_API_URL || 'https://app.adsmash.org/api'; // Live URL

const CreatePassword: React.FC = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [email, setEmail] = useState<string | null>(null);
  const [name, setName] = useState<string | null>(null);
  const [setupToken, setSetupToken] = useState<string | null>(null);
  const [passwordRequirements, setPasswordRequirements] = useState({
    minLength: false,
    maxLength: false,
    hasUpperCase: false,
    hasLowerCase: false,
    hasNumber: false,
    hasSpecialChar: false,
    hasNoSpaces: false,
  });

  const navigate = useNavigate();
  const { login } = useAuth();

  useEffect(() => {
    // Check if token and email are in query params (e.g. from onboarding reminder email)
    const queryParams = new URLSearchParams(window.location.search);
    const queryToken = queryParams.get('token') || sessionStorage.getItem('adsmash_setup_token');
    const queryEmail = queryParams.get('email') || sessionStorage.getItem('pending_signup_email');

    if (queryToken) {
      setSetupToken(queryToken);
      sessionStorage.setItem('adsmash_setup_token', queryToken);
      if (queryEmail) {
        setEmail(queryEmail);
        sessionStorage.setItem('pending_signup_email', queryEmail);
      }
      setName('AdSmash Member');

      // Scrub token and email from the URL address bar immediately
      if (queryParams.has('token') || queryParams.has('email')) {
        window.history.replaceState({}, document.title, window.location.pathname);
      }
      return;
    }

    // Get email and name from sessionStorage
    const storedEmail = sessionStorage.getItem('pending_signup_email');
    const storedName = sessionStorage.getItem('pending_signup_name');

    if (!storedEmail) {
      // If no email found, redirect to payment page
      navigate('/payment');
      return;
    }

    setEmail(storedEmail);
    setName(storedName);
  }, [navigate]);

  useEffect(() => {
    // Validate password requirements
    setPasswordRequirements({
      minLength: password.length >= 8,
      maxLength: password.length <= 24,
      hasUpperCase: /[A-Z]/.test(password),
      hasLowerCase: /[a-z]/.test(password),
      hasNumber: /[0-9]/.test(password),
      hasSpecialChar: /[^A-Za-z0-9]/.test(password),
      hasNoSpaces: !/\s/.test(password),
    });
  }, [password]);

  const isPasswordValid = Object.values(passwordRequirements).every(Boolean);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!password || !confirmPassword) {
      setError('Please fill in all fields');
      return;
    }

    if (!isPasswordValid) {
      setError('Password does not meet all requirements');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!email || (!name && !setupToken)) {
      setError('Missing email or name information. Please start over.');
      navigate('/payment');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    // If user arrived from onboarding reminder email with setupToken, complete password directly
    if (setupToken) {
      try {
        const response = await axios.post(
          `${API_URL}/auth/complete-password`,
          {
            email: email.trim(),
            password,
            setupToken,
          },
          {
            timeout: 10000,
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );

        if (response.data.success) {
          const { token, exchangeTicket } = response.data;
          setToken(token);
          localStorage.setItem('subscription_status', 'active');
          localStorage.setItem('subscription_plan', 'PREMIUM');
          sessionStorage.removeItem('adsmash_setup_token');

          setSuccess(true);
          setIsSubmitting(false);

          setTimeout(() => {
            if (exchangeTicket) {
              window.location.href = `/post-signup?ticket=${encodeURIComponent(exchangeTicket)}`;
            } else {
              navigate('/post-signup');
            }
          }, 2000);
          return;
        } else {
          throw new Error(response.data.error || 'Failed to complete password setup');
        }
      } catch (err: any) {
        console.error('Password completion error:', err);
        const errorMessage =
          err.response?.data?.error ||
          err.response?.data?.message ||
          err.message ||
          'Failed to set password. Please try again.';
        setError(errorMessage);
        setIsSubmitting(false);
        return;
      }
    }

    try {
      // Split name into firstName and lastName
      const nameParts = (name || '').trim().split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      console.log('Attempting to register user:', { email: email.trim(), firstName, lastName });
      console.log('API URL:', `${API_URL}/auth/register`);

      // Register user with the backend
      const response = await axios.post(`${API_URL}/auth/register`, {
        email: email.trim(),
        password,
        firstName,
        lastName,
      }, {
        timeout: 10000, // 10 second timeout
        headers: {
          'Content-Type': 'application/json',
        },
      });

      console.log('Registration response:', response.data);

      if (response.data.success) {
        const { token, user } = response.data;

        // Store token via helper so contexts are notified
        setToken(token);

        // Check if there's a pending payment method (from payment page)
        const pendingPaymentMethodId = sessionStorage.getItem('pending_payment_method_id');
        const pendingPriceId = sessionStorage.getItem('pending_price_id');

        if (pendingPaymentMethodId && pendingPriceId) {
          // Complete the Stripe subscription payment
          try {
            console.log('Completing subscription payment for new user...');
            
            const subscriptionResponse = await axios.post(
              `${API_URL}/subscription/create-subscription`,
              {
                paymentMethodId: pendingPaymentMethodId,
                priceId: pendingPriceId,
              },
              {
                headers: {
                  Authorization: `Bearer ${token}`
                }
              }
            );
            
            console.log('Subscription response:', subscriptionResponse.data);
            
            if (subscriptionResponse.data.success) {
              const subscription = subscriptionResponse.data.subscription;
              console.log('Subscription created successfully');
              
              // Update localStorage
              if (subscription && subscription.status === 'active') {
                localStorage.setItem('subscription_status', 'active');
                localStorage.setItem('subscription_plan', subscription.plan || 'PREMIUM');
                console.log('Subscription confirmed and saved to localStorage');
              }
              
              // Clear payment method from sessionStorage
              sessionStorage.removeItem('pending_payment_method_id');
              sessionStorage.removeItem('pending_price_id');
            } else {
              console.error('Subscription creation failed:', subscriptionResponse.data);
            }
          } catch (subscriptionError: any) {
            console.error('Error creating subscription:', subscriptionError);
            console.error('Error response:', subscriptionError.response?.data);
            // Continue with normal flow even if subscription fails
          }
        } else {
          const guestCustomerId = sessionStorage.getItem('guest_customer_id');
          const guestSubscriptionId = sessionStorage.getItem('guest_subscription_id');

          if (guestCustomerId && guestSubscriptionId) {
            try {
              console.log('Linking guest subscription to new account...');

              const linkResponse = await axios.post(
                `${API_URL}/subscription/link-guest-subscription`,
                {
                  stripeCustomerId: guestCustomerId,
                  subscriptionId: guestSubscriptionId,
                },
                {
                  headers: {
                    Authorization: `Bearer ${token}`
                  }
                }
              );

              console.log('Link subscription response:', linkResponse.data);

              if (linkResponse.data.success) {
                const linkedSubscription = linkResponse.data.subscription;
                
                if (linkedSubscription && linkedSubscription.status === 'active') {
                  localStorage.setItem('subscription_status', 'active');
                  localStorage.setItem('subscription_plan', linkedSubscription.plan || 'PREMIUM');
                  console.log('Guest subscription linked and saved to localStorage');
                }

                sessionStorage.removeItem('guest_customer_id');
                sessionStorage.removeItem('guest_subscription_id');
                sessionStorage.removeItem('pending_price_id');
              }
            } catch (linkError: any) {
              console.error('Error linking guest subscription:', linkError);
              console.error('Error response:', linkError.response?.data);
            }
          } else {
            const paypalOrderId = sessionStorage.getItem('paypal_order_id');
            const paypalCaptureId = sessionStorage.getItem('paypal_capture_id');

            if (paypalOrderId && paypalCaptureId) {
              try {
                console.log('Finalizing captured PayPal payment for new account...');

                const finalizeResponse = await axios.post(
                  `${API_URL}/subscription/finalize-paypal-order`,
                  {
                    orderId: paypalOrderId,
                    captureId: paypalCaptureId,
                  },
                  {
                    headers: {
                      Authorization: `Bearer ${token}`
                    }
                  }
                );

                if (finalizeResponse.data.success && finalizeResponse.data.user) {
                  const subscription = finalizeResponse.data.user.subscription;

                  if (subscription && subscription.status === 'active' && subscription.plan === 'PREMIUM') {
                    localStorage.setItem('subscription_status', 'active');
                    localStorage.setItem('subscription_plan', 'PREMIUM');
                    console.log('PayPal premium membership confirmed and saved to localStorage');
                  }

                  sessionStorage.removeItem('paypal_order_id');
                  sessionStorage.removeItem('paypal_capture_id');
                }
              } catch (finalizeError: any) {
                console.error('Error finalizing PayPal payment:', finalizeError);
                console.error('Error response:', finalizeError.response?.data);
              }
            }
          }
        }

        // Clear sessionStorage
        sessionStorage.removeItem('pending_signup_email');
        sessionStorage.removeItem('pending_signup_name');

        // Show success and redirect
        setSuccess(true);
        setIsSubmitting(false);
        
        setTimeout(() => {
          navigate('/post-signup');
        }, 2000);
      } else {
        throw new Error(response.data.error || 'Failed to create account');
      }
    } catch (err: any) {
      console.error('Account creation error:', err);
      console.error('Error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
        statusText: err.response?.statusText,
      });

      let errorMessage = 'Failed to create account. Please try again.';
      
      if (err.response) {
        // Server responded with error
        errorMessage = err.response.data?.error || 
                      err.response.data?.message || 
                      `Server error: ${err.response.status} ${err.response.statusText}`;
      } else if (err.request) {
        // Request was made but no response received
        errorMessage = 'Unable to connect to server. Please check your internet connection and try again.';
      } else {
        // Error setting up the request
        errorMessage = err.message || 'An unexpected error occurred.';
      }

      setError(errorMessage);
      setIsSubmitting(false);
    }
  };

  if (!email) {
    return (
      <div
        className="min-h-screen flex flex-col"
        style={{ background: 'linear-gradient(to bottom, #0c0d1f, #1b1324)' }}
      >
        <main className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-400"></div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ background: 'linear-gradient(to bottom, #0c0d1f, #1b1324)' }}
    >
      <header className="w-full px-6 md:px-12 lg:px-20 py-6 flex items-center">
        <Link to="/" className="flex items-center gap-2">
          <img src="/images/logo.png" alt="AdSmash Logo" className="h-10 md:h-12" />
        </Link>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 md:px-12 lg:px-20 py-12">
        <div className="max-w-md w-full">
          <div className="bg-white/5 backdrop-blur rounded-3xl p-8 border border-white/10 shadow-2xl">
            <h1 className="text-3xl font-bold text-white mb-2">Create your password</h1>
            <p className="text-white/70 mb-8">
              Your payment was successful! Now create a secure password for your account.
            </p>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-white/80 mb-2">
                  Email address
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  disabled
                  className="w-full rounded-lg border border-white/20 bg-white/5 px-4 py-3 text-white/60 cursor-not-allowed"
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-white/80 mb-2">
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="w-full rounded-lg border border-white/20 bg-white/5 px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition"
                  required
                />
                
                {/* Password Requirements */}
                <div className="mt-3 space-y-2">
                  <p className="text-xs text-white/60 font-medium">Password must contain:</p>
                  <div className="space-y-1.5">
                    <div className={`flex items-center gap-2 text-xs ${passwordRequirements.minLength ? 'text-green-400' : 'text-white/50'}`}>
                      <span>{passwordRequirements.minLength ? '✓' : '○'}</span>
                      <span>At least 8 characters</span>
                    </div>
                    <div className={`flex items-center gap-2 text-xs ${passwordRequirements.maxLength ? 'text-green-400' : 'text-white/50'}`}>
                      <span>{passwordRequirements.maxLength ? '✓' : '○'}</span>
                      <span>At most 24 characters</span>
                    </div>
                    <div className={`flex items-center gap-2 text-xs ${passwordRequirements.hasUpperCase ? 'text-green-400' : 'text-white/50'}`}>
                      <span>{passwordRequirements.hasUpperCase ? '✓' : '○'}</span>
                      <span>One uppercase letter</span>
                    </div>
                    <div className={`flex items-center gap-2 text-xs ${passwordRequirements.hasLowerCase ? 'text-green-400' : 'text-white/50'}`}>
                      <span>{passwordRequirements.hasLowerCase ? '✓' : '○'}</span>
                      <span>One lowercase letter</span>
                    </div>
                    <div className={`flex items-center gap-2 text-xs ${passwordRequirements.hasNumber ? 'text-green-400' : 'text-white/50'}`}>
                      <span>{passwordRequirements.hasNumber ? '✓' : '○'}</span>
                      <span>One number</span>
                    </div>
                    <div className={`flex items-center gap-2 text-xs ${passwordRequirements.hasSpecialChar ? 'text-green-400' : 'text-white/50'}`}>
                      <span>{passwordRequirements.hasSpecialChar ? '✓' : '○'}</span>
                      <span>One special character (!@#$%^&*)</span>
                    </div>
                    <div className={`flex items-center gap-2 text-xs ${passwordRequirements.hasNoSpaces ? 'text-green-400' : 'text-white/50'}`}>
                      <span>{passwordRequirements.hasNoSpaces ? '✓' : '○'}</span>
                      <span>No spaces</span>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-white/80 mb-2">
                  Confirm Password
                </label>
                <input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your password"
                  className="w-full rounded-lg border border-white/20 bg-white/5 px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition"
                  required
                />
                {confirmPassword && password !== confirmPassword && (
                  <p className="mt-2 text-xs text-red-400">Passwords do not match</p>
                )}
              </div>

              {success && (
                <div className="rounded-lg border border-green-400/50 bg-green-500/20 px-4 py-3 text-sm text-green-300 flex items-center gap-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Password created successfully! Redirecting to next step...</span>
                </div>
              )}

              {error && (
                <div className="rounded-lg border border-red-400/50 bg-red-500/20 px-4 py-3 text-sm text-red-300">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting || !isPasswordValid || password !== confirmPassword}
                className="btn-adsmash-primary w-full inline-flex items-center justify-center rounded-lg px-6 py-4 text-white font-semibold shadow-lg transition disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Creating account...' : 'Create account'}
              </button>
            </form>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default CreatePassword;
